import React from 'react';
import styled from 'styled-components';
import { palette } from '@solvhealth/jigsaw-v1';
import { CategoryIds, TileIterator, TileUsers, TileType, CategoryData } from './tileEnums';
import { getAgeFromBirthDate } from '../../../../../core/util/date';
import HealthChecklistContainer from '../CategoryContainers/HealthChecklistContainer';
import { remindersSpec } from '../../../../Account/Profile/Reminders/util/reminders';
import { colors } from '../../../../../constants/colors';
import {
  hasNoFamilyDeductible,
  hasNoIndividualDeductible,
} from '../../../../Account/Profile/Insurance/util/insuranceDetails';
import { deductibleProgressDescriptions } from '../../../../Account/Profile/Insurance/util/descriptions/deductible';
import { TileAction, TileRules } from '../../../../Account/Profile/util/tileEnums';
import {
  INSURANCE_SWIMLANE_DEDUCTIBLE_TAPPED,
  INSURANCE_SWIMLANE_OUT_OF_POCKET_MAX_TAPPED,
} from '../../../../../core/analytics/events';
import { getCopayCoinsuranceSwimlaneTitle } from '../../../../Account/Profile/Insurance/util/copayCoinsurance';
import FlexContainer from '../../../../Account/Profile/Insurance/CategoryContainers/FlexContainer';
import { copayCoinsuranceDescriptions } from '../../../../Account/Profile/Insurance/util/descriptions/copayCoinsurance';
import { outOfPocketMaxDescriptions } from '../../../../Account/Profile/Insurance/util/descriptions/outOfPocketMax';
import BenefitsPhoneSubtext from '../../../../Account/Profile/Insurance/util/insurance_insight_subtexts/BenefitsPhoneSubtext';
import { insightDescriptions } from '../../../../Account/Profile/Insurance/util/descriptions/insights';
import { CALL_INSURANCE_MODAL } from '../../../../../constants/modalIds';
import { copayCoinsuranceModalDetailsDescriptions } from '../../../../Account/Profile/Insurance/util/descriptions/copayCoinsuranceModalDetails';

const Link = styled.span.withConfig({ componentId: 'sc-179txpj-0' })`
  cursor: pointer;
  color: ${palette.crunchberry.base};
`;

export const categories = [
  {
    id: CategoryIds.HEALTH_CHECKLIST,
    requiredData: [CategoryData.USER_PROFILE],
    title: ({ userProfile }: any) =>
      `Staying healthy at ${getAgeFromBirthDate(userProfile.birth_date)}`,
    description: (
      <span>
        Complete your <Link>healthcare</Link> checklist
      </span>
    ),
    iterator: TileIterator.HEALTH_CHECKLIST,
    Container: HealthChecklistContainer,
    columnCount: { sm: 1.2, md: 3.3, lg: 4.2 },
    tiles: Object.keys(remindersSpec).map((key) => ({
      id: key,
      visibility: TileUsers.ALL,
      allProps: true,
      tileType: TileType.HEALTH_CHECKLIST,
      ...remindersSpec[key],
    })),
  },
  {
    id: CategoryIds.DEDUCTIBLE_PROGRESS,
    requiredData: [CategoryData.INSURANCE_PROFILE, CategoryData.INSURANCE_DETAILS],
    title: () => 'Deductible',
    description: (props: any) => {
      if (
        // eslint-disable-next-line react/prop-types
        hasNoIndividualDeductible(props.insuranceDetails) &&
        // eslint-disable-next-line react/prop-types
        hasNoFamilyDeductible(props.insuranceDetails)
      ) {
        return (
          <span>
            Your plan has <b>no deductible</b> so insurance will always cover part of your care.
          </span>
        );
      }
      return (
        <span>
          <b>You pay the full cost</b> for covered treatments, until you meet your plan's
          deductible.
        </span>
      );
    },
    iterator: TileIterator.TILES,
    columnCount: { sm: 1.8, md: 3, lg: 4.3 },
    tiles: deductibleProgressDescriptions.map((tile) => ({
      id: tile.id,
      tileType: TileType.INSURANCE_PROGRESS,
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'description' does not exist on type '{ i... Remove this comment to see the full error message
      title: tile.description,
      subtext: tile.subtext,
      visibility: TileUsers.ALL,
      rules: tile.rules ? tile.rules : [],
      isInNetwork: tile.isInNetwork,
      allProps: true,
      action: TileAction.TRACK_CLICK,
      eventName: INSURANCE_SWIMLANE_DEDUCTIBLE_TAPPED,
    })),
  },
  {
    id: CategoryIds.COPAY_COINSURANCE,
    requiredData: [CategoryData.INSURANCE_PROFILE, CategoryData.INSURANCE_DETAILS],
    title: getCopayCoinsuranceSwimlaneTitle,
    description: () => 'Get a sense of your benefits and payment responsibility.',
    iterator: TileIterator.TILES,
    columnCount: { sm: 1.2, md: 3.3, lg: 4 },
    Container: FlexContainer,
    tiles: copayCoinsuranceDescriptions.map((tile) => ({
      id: tile.id,
      tileType: tile.type,
      title: tile.description,
      subtext: tile.subtext,
      icon: tile.icon,
      visibility: TileUsers.ALL,
      rules: tile.rules,
      action: tile.action,
      providerType: tile.providerType,
      allProps: true,
      modalId: tile.modalId,
      modalValue: tile.modalValue,
      shouldShowBookCta: tile.shouldShowBookCta,
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'bookCtaCopy' does not exist on type '{ i... Remove this comment to see the full error message
      bookCtaCopy: tile.bookCtaCopy,
    })),
  },
  {
    id: CategoryIds.OUT_OF_POCKET_MAX,
    requiredData: [CategoryData.INSURANCE_PROFILE, CategoryData.INSURANCE_DETAILS],
    title: () => 'Out of pocket max',
    description: () => (
      <span>
        Once you've hit your OOP max, <b>you pay nothing</b> for covered treatments.
      </span>
    ),
    iterator: TileIterator.TILES,
    columnCount: { sm: 1.8, md: 3, lg: 4.3 },
    tiles: outOfPocketMaxDescriptions.map((tile) => ({
      id: tile.id,
      tileType: TileType.INSURANCE_PROGRESS,
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'description' does not exist on type '{ i... Remove this comment to see the full error message
      title: tile.description,
      subtext: tile.subtext,
      visibility: TileUsers.ALL,
      rules: tile.rules ? tile.rules : [],
      action: TileAction.TRACK_CLICK,
      isInNetwork: tile.isInNetwork,
      allProps: true,
      eventName: INSURANCE_SWIMLANE_OUT_OF_POCKET_MAX_TAPPED,
    })),
  },

  {
    id: CategoryIds.COPAY_COINSURANCE_DETAILS_MODAL,
    requiredData: [CategoryData.INSURANCE_PROFILE, CategoryData.INSURANCE_DETAILS],
    title: null,
    description: null,
    iterator: TileIterator.TILES,
    columnCount: { sm: 1.2, md: 1.4, lg: 1.4 },
    tiles: copayCoinsuranceModalDetailsDescriptions.map((tile: any) => ({
      id: tile.id,
      tileType: TileType.COPAY_COINSURANCE_DETAILS_MODAL,
      title: tile.description,
      subtext: tile.subtext,
      icon: tile.icon,
      visibility: TileUsers.ALL,
      rules: [],
      isInNetwork: tile.isInNetwork,
      allProps: true,
      action: TileAction.TRACK_CLICK,
      eventName: INSURANCE_SWIMLANE_DEDUCTIBLE_TAPPED,
    })),
  },
];
